import type { Immutable } from "immer";
import React from "react";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { cmsStyleHook } from "tsi-common-react/src/utils/cmsStyleHook";

import type { HTMLAdBarBlock as HTMLAdBarBlockValue } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type Value = HTMLAdBarBlockValue;
type ContentItem = NonNullable<Value["content"]>[number];

/**
 * Faithful port of the legacy server-rendered ``cms/blocks/html-ad-bar-block.html``.
 *
 * The legacy ``<section class="html-ad-bar-block ...">`` iterated its ``content``
 * StreamBlock and rendered each item through its own block template:
 *
 *   - ``image_link`` -> ``link-image-block.html``:
 *     ``<a href="{{ value.link.url }}">{% wagtail_image value.image
 *     specs='original' class="responsive-img" %}</a>`` (the link href is empty
 *     when no Link snippet is set; the image uses the ``original`` rendition).
 *   - ``html`` -> ``RawHTMLBlock``, which emits its raw HTML verbatim.
 *
 * The ``u-full-bleed`` modifier is applied when ``full_bleed`` is set (legacy
 * ``{% if value.full_bleed %}u-full-bleed{% endif %}``).
 */
const ContentItemElem = (
    props: Immutable<{ item: ContentItem }>,
): React.JSX.Element | null => {
    const item = props.item;
    if (item.type === "image_link") {
        const v = item.value;
        const image = v.image;
        return (
            <a href={v.link?.url ?? ""}>
                {image ? (
                    <img
                        className="responsive-img"
                        src={image.renditions["original"]?.url ?? image.url}
                        alt={image.title}
                        loading="lazy"
                        decoding="async"
                    />
                ) : null}
            </a>
        );
    }
    // ``html`` -> RawHTMLBlock: emit the author-supplied raw HTML
    // (CMS-trusted content, matching {% include_block %} of a RawHTMLBlock).
    // The shared component strips the pre-wrapped ``raw-html-block`` div so
    // the host is that single element, matching the legacy DOM.
    return <RawHTMLBlock value={item.value} />;
};

export const HTMLAdBarBlock = (
    props: Immutable<{ value: HTMLAdBarBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    return (
        <section
            className={concatClassNames([
                cmsStyleHook("html-ad-bar-block"),
                styles.htmlAdBarBlock,
                v.full_bleed ? "u-full-bleed" : "",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            {(v.content ?? []).map((item, i) => (
                <ContentItemElem key={i} item={item} />
            ))}
        </section>
    );
};
